/*
 * Endless image marquee.
 *
 * The track holds two identical copies of the image list. Scroll position is wrapped at
 * half the track width, so the strip never reaches an end in either direction — drag it
 * left or right as long as you like. Auto-drift is just a slow, continuous scroll.
 *
 * Native scrolling does the heavy lifting: touch momentum, trackpad gestures and
 * keyboard scrolling all work without any code from us.
 */
(function () {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function initMarquee(viewport) {
    if (viewport.dataset.marqueeReady === 'true') return;
    viewport.dataset.marqueeReady = 'true';

    const track = viewport.querySelector('.img-marquee__track');
    if (!track) return;

    const speed = parseFloat(viewport.dataset.speed) || 30; // px per second
    const pauseOnHover = viewport.dataset.pauseOnHover === 'true';
    let autoplay = viewport.dataset.autoplay === 'true';

    const originals = Array.from(track.children);
    let baseWidth = 0;
    let paused = false;
    let dragging = false;
    let lastFrame = null;
    let rafId = null;

    // The markup ships one copy of the images. However many there are, the strip has to
    // be at least one screen wider than a single set, or there is nothing to scroll into
    // and the loop cannot happen at all. So: measure one set, then clone whole sets
    // until there is enough runway.
    function layout() {
      if (!originals.length) return;

      // Nothing is laid out yet — hidden tab, display:none ancestor, pre-paint. Measuring
      // now would record a meaningless base width and make the wrap jump at a few pixels.
      // The ResizeObserver below calls back as soon as the strip has a real width.
      if (viewport.clientWidth <= 0) return;

      track.querySelectorAll('[data-marquee-clone]').forEach((clone) => clone.remove());

      baseWidth = track.scrollWidth;
      if (baseWidth <= 0) return;

      const target = baseWidth + viewport.clientWidth;
      let guard = 0;

      while (track.scrollWidth < target && guard < 30) {
        const fragment = document.createDocumentFragment();

        originals.forEach((item) => {
          const clone = item.cloneNode(true);
          clone.setAttribute('data-marquee-clone', '');
          clone.setAttribute('aria-hidden', 'true');
          clone.removeAttribute('id');
          clone.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
          fragment.appendChild(clone);
        });

        track.appendChild(fragment);
        guard++;
      }
    }

    // Keeps the scroll position inside the first set. Every later set is identical, so
    // the jump is invisible.
    function wrap() {
      if (baseWidth <= 0) return;
      if (viewport.scrollLeft >= baseWidth) viewport.scrollLeft -= baseWidth;
      else if (viewport.scrollLeft <= 0) viewport.scrollLeft += baseWidth;
    }

    function frame(timestamp) {
      if (lastFrame === null) lastFrame = timestamp;
      const elapsed = (timestamp - lastFrame) / 1000;
      lastFrame = timestamp;

      if (autoplay && !paused && !dragging && !reduceMotion.matches) {
        viewport.scrollLeft += speed * elapsed;
        wrap();
      }

      rafId = window.requestAnimationFrame(frame);
    }

    function start() {
      if (rafId !== null) return;
      lastFrame = null;
      rafId = window.requestAnimationFrame(frame);
    }

    function stop() {
      if (rafId === null) return;
      window.cancelAnimationFrame(rafId);
      rafId = null;
    }

    viewport.addEventListener('scroll', wrap, { passive: true });

    if (pauseOnHover) {
      viewport.addEventListener('pointerenter', () => { paused = true; });
      viewport.addEventListener('pointerleave', () => { paused = false; });
    }

    // Pause while the strip has keyboard focus, so arrow-key scrolling is not fighting
    // the drift.
    viewport.addEventListener('focusin', () => { paused = true; });
    viewport.addEventListener('focusout', () => { paused = false; });

    /* ------------------------------------------------------------------ dragging */

    let pointerId = null;
    let startX = 0;
    let startScroll = 0;
    let moved = 0;

    viewport.classList.add('is-draggable');

    viewport.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch') return; // native touch scrolling is better
      pointerId = event.pointerId;
      dragging = true;
      moved = 0;
      startX = event.clientX;
      startScroll = viewport.scrollLeft;
      viewport.classList.add('is-dragging');
    });

    viewport.addEventListener('pointermove', (event) => {
      if (!dragging || event.pointerId !== pointerId) return;
      const delta = event.clientX - startX;
      moved = Math.abs(delta);
      viewport.scrollLeft = startScroll - delta;
      wrap();
    });

    function endDrag(event) {
      if (!dragging || (pointerId !== null && event.pointerId !== pointerId)) return;
      dragging = false;
      pointerId = null;
      viewport.classList.remove('is-dragging');
    }

    viewport.addEventListener('pointerup', endDrag);
    viewport.addEventListener('pointercancel', endDrag);
    viewport.addEventListener('pointerleave', endDrag);

    // A drag that ends on top of a link should not also count as a click on it.
    viewport.addEventListener(
      'click',
      (event) => {
        if (moved > 5) {
          event.preventDefault();
          event.stopPropagation();
          moved = 0;
        }
      },
      true
    );

    /* -------------------------------------------------------------------- start */

    layout();

    let layoutTimer = null;
    function relayout() {
      clearTimeout(layoutTimer);
      layoutTimer = setTimeout(() => {
        layout();
        wrap();
      }, 150);
    }

    window.addEventListener('resize', relayout);

    // Observe the viewport, never the track: layout() changes the track's width, so
    // watching it would retrigger layout forever.
    if ('ResizeObserver' in window) new ResizeObserver(relayout).observe(viewport);

    track.querySelectorAll('img').forEach((image) => {
      if (!image.complete) image.addEventListener('load', relayout, { once: true });
    });

    // No point animating a strip nobody is looking at.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        entries.forEach((entry) => (entry.isIntersecting ? start() : stop()));
      }).observe(viewport);
    } else {
      start();
    }

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stop();
      else start();
    });
  }

  function initAll() {
    document.querySelectorAll('.img-marquee__viewport').forEach(initMarquee);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', initAll);
})();
